import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["disableOutsidePointerEvents", "onEscapeKeyDown", "onPointerDownOutside", "onFocusOutside", "onInteractOutside", "onDismiss"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { CONTEXT_UPDATE } from './constants';
import { dispatchUpdate, useFocusOutside } from './useFocusOutside';
import { usePointerDownOutside } from './usePointerDownOutside';
import { DomPrimitive } from '../DomPrimitive';
import { useComposedRefs } from '../../hooks/useComposedRefs';
import { useEscapeKeydown } from '../../hooks/useEscapeKeyodwn';
import { composeEventHandlers } from '../../utils/composeEventHandlers';
import { jsx as _jsx } from "react/jsx-runtime";
const DISMISSABLE_LAYER_NAME = 'DismissableLayer';
let originalBodyPointerEvents;
const DismissableLayerContext = /*#__PURE__*/React.createContext({
  layers: new Set(),
  layersWithOutsidePointerEventsDisabled: new Set(),
  branches: new Set()
});
export const DismissableLayer = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  var _node$ownerDocument;
  const {
      disableOutsidePointerEvents = false,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      onDismiss
    } = props,
    layerProps = _objectWithoutProperties(props, _excluded);
  const context = React.useContext(DismissableLayerContext);
  const [node, setNode] = React.useState(null);
  const ownerDocument = (_node$ownerDocument = node === null || node === void 0 ? void 0 : node.ownerDocument) !== null && _node$ownerDocument !== void 0 ? _node$ownerDocument : globalThis === null || globalThis === void 0 ? void 0 : globalThis.document;
  const [, force] = React.useState({});
  const composedRefs = useComposedRefs(forwardedRef, node => setNode(node));
  const layers = Array.from(context.layers);
  const [highestLayerWithOutsidePointerEventsDisabled] = [...context.layersWithOutsidePointerEventsDisabled].slice(-1); // prettier-ignore
  const highestLayerWithOutsidePointerEventsDisabledIndex = layers.indexOf(highestLayerWithOutsidePointerEventsDisabled); // prettier-ignore
  const index = node ? layers.indexOf(node) : -1;
  const isBodyPointerEventsDisabled = context.layersWithOutsidePointerEventsDisabled.size > 0;
  const isPointerEventsEnabled = index >= highestLayerWithOutsidePointerEventsDisabledIndex;
  const pointerDownOutside = usePointerDownOutside(event => {
    const target = event.target;
    const isPointerDownOnBranch = [...context.branches].some(branch => branch.contains(target));
    if (!isPointerEventsEnabled || isPointerDownOnBranch) {
      return;
    }
    onPointerDownOutside === null || onPointerDownOutside === void 0 || onPointerDownOutside(event);
    onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
    if (!event.defaultPrevented) {
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }
  }, ownerDocument);
  const focusOutside = useFocusOutside(event => {
    const target = event.target;
    const isFocusInBranch = [...context.branches].some(branch => branch.contains(target));
    if (isFocusInBranch) {
      return;
    }
    onFocusOutside === null || onFocusOutside === void 0 || onFocusOutside(event);
    onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
    if (!event.defaultPrevented) {
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }
  }, ownerDocument);
  useEscapeKeydown(event => {
    const isHighestLayer = index === context.layers.size - 1;
    if (!isHighestLayer) {
      return;
    }
    onEscapeKeyDown === null || onEscapeKeyDown === void 0 || onEscapeKeyDown(event);
    if (!event.defaultPrevented && onDismiss) {
      event.preventDefault();
      onDismiss();
    }
  }, ownerDocument);
  React.useEffect(() => {
    if (!node) {
      return;
    }
    if (disableOutsidePointerEvents) {
      if (context.layersWithOutsidePointerEventsDisabled.size === 0) {
        originalBodyPointerEvents = ownerDocument.body.style.pointerEvents;
        ownerDocument.body.style.pointerEvents = 'none';
      }
      context.layersWithOutsidePointerEventsDisabled.add(node);
    }
    context.layers.add(node);
    dispatchUpdate();
    return () => {
      if (disableOutsidePointerEvents && context.layersWithOutsidePointerEventsDisabled.size === 1) {
        ownerDocument.body.style.pointerEvents = originalBodyPointerEvents;
      }
    };
  }, [node, ownerDocument, disableOutsidePointerEvents, context]);

  /**
   * Мы намеренно предотвращаем объединение этого эффекта с эффектом `disableOutsidePointerEvents`
   * потому что изменение `disableOutsidePointerEvents` удалило бы этот слой из стека
   * и добавило его снова в конец, так что порядок слоев не был бы _порядком создания_.
   * Мы хотим, чтобы они удалялись из контекстных стеков только при размонтировании.
   */
  React.useEffect(() => {
    return () => {
      if (!node) {
        return;
      }
      context.layers.delete(node);
      context.layersWithOutsidePointerEventsDisabled.delete(node);
      dispatchUpdate();
    };
  }, [node, context]);
  React.useEffect(() => {
    const handleUpdate = () => force({});
    document.addEventListener(CONTEXT_UPDATE, handleUpdate);
    return () => document.removeEventListener(CONTEXT_UPDATE, handleUpdate);
  }, []);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({}, layerProps), {}, {
    ref: composedRefs,
    style: _objectSpread({
      pointerEvents: isBodyPointerEventsDisabled ? isPointerEventsEnabled ? 'auto' : 'none' : undefined
    }, props.style),
    onFocusCapture: composeEventHandlers(props.onFocusCapture, focusOutside.onFocusCapture),
    onBlurCapture: composeEventHandlers(props.onBlurCapture, focusOutside.onBlurCapture),
    onPointerDownCapture: composeEventHandlers(props.onPointerDownCapture, pointerDownOutside.onPointerDownCapture)
  }));
});
DismissableLayer.displayName = DISMISSABLE_LAYER_NAME;

/* -------------------------------------------------------------------------------------------------
 * DismissableLayerBranch
 * -----------------------------------------------------------------------------------------------*/

const BRANCH_NAME = 'DismissableLayerBranch';
const DismissableLayerBranch = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const context = React.useContext(DismissableLayerContext);
  const ref = React.useRef(null);
  const composedRefs = useComposedRefs(forwardedRef, ref);
  React.useEffect(() => {
    const node = ref.current;
    if (node) {
      context.branches.add(node);
      return () => {
        context.branches.delete(node);
      };
    }
    return () => {};
  }, [context.branches]);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({}, props), {}, {
    ref: composedRefs
  }));
});
DismissableLayerBranch.displayName = BRANCH_NAME;